// System
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { BaseSyntheticEvent, useCallback, useEffect, useMemo } from 'react';
// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  setShowProviderDrawer,
  setShowCancelProviderDrawer,
  setSelectedProvider,
  setShowDeleteProviderModal,
  setSelectedCurrency,
  setShowCurrencyModal,
} from '@Modal/reducers/providers/drawer/providerDrawerSlice';
// Form
import { useFormState } from 'react-hook-form';
import {
  useManageSetupProviderSchema,
  useSetupProviderForm,
} from '@Forms/providers/SetupProviderFormContext';
// Types
import { AppDispatch } from 'src/app/store';
import { StoreInterface } from 'src/app/types';
import {
  // useDeleteProviderMutation,
  useGetProviderLazyQuery,
  useGetProvidersQuery,
  useUpdateProviderMutation,
} from '@Src/generated.graphql';
import { useSetupProviderUseCaseMapper } from '@Hooks/providers/SetupProvider/useSetupProviderUseCaseMapper';
import { useSetupProviderUseCaseSchema } from '@Hooks/providers/SetupProvider/useSetupProviderUseCaseSchema';
import { useGetProviderConfigsQuery } from '@Src/generated.graphql';
import Cookies from 'js-cookie';

export const useProviderDrawer = () => {
  const [getProvider] = useGetProviderLazyQuery();
  // const [deleteProvider] = useDeleteProviderMutation();
  const { refetch: refetchProviders } = useGetProvidersQuery();
  const [updateProvider] = useUpdateProviderMutation();

  // Redux
  const dispatch = useDispatch<AppDispatch>();

  const {
    showProviderDrawer,
    showCancelProviderDrawer,
    selectedProviderData,
    showDeleteProviderModal,
    showCurrencyModal,
    selectedCurrency,
  } = useSelector((state: StoreInterface) => state?.providerDrawer);
  const rgs = useSelector((state: StoreInterface) => state.general.rgs) || 'rgs1';

  const { refetch: refetchProviderConfigs } = useGetProviderConfigsQuery({
    variables: { id: selectedProviderData?.id },
  });
  const { setSchema } = useManageSetupProviderSchema();
  const mapper = useSetupProviderUseCaseMapper(selectedProviderData);
  const schema = useSetupProviderUseCaseSchema();

  // Other Hooks
  const { enqueueSnackbar } = useSnackbar();

  // Handle Form Cancels
  const handleCreateCancel = () => {
    if (isDirty) {
      return dispatch(setShowCancelProviderDrawer(true));
    } else {
      return dispatch(setShowProviderDrawer(false));
    }
  };

  const handleCancelTakeMeBack = (action: string) => {
    if (action === 'back') {
      dispatch(setShowCancelProviderDrawer(false));
      dispatch(setShowProviderDrawer(true));
    } else {
      reset();
      dispatch(setShowCancelProviderDrawer(false));
      dispatch(setShowProviderDrawer(false));
    }
  };

  const handleCancelChanges = () => {
    dispatch(setShowCancelProviderDrawer(false));
    dispatch(setShowProviderDrawer(false));
    reset();
  };

  const setProviderDrawerMapperHandler = useCallback(async () => {
    setSchema(schema);
    const formState = await mapper.toFormState(selectedProviderData.id);
    reset(formState);
  }, [selectedProviderData]);

  useEffect(() => {
    if (selectedProviderData) {
      setProviderDrawerMapperHandler();
    }
  }, [selectedProviderData, setProviderDrawerMapperHandler]);

  // Form Controls
  const { control, handleSubmit, reset } = useSetupProviderForm();
  const { isDirty, errors } = useFormState({ control });
  const handleProviderRowClick = async (id: string) => {
    try {
      const { data } = await getProvider({ variables: { providerId: id } });
      if (data && data.provider) {
        await dispatch(setSelectedProvider(data.provider));
      }
    } catch (error) {
      enqueueSnackbar(error, {
        autoHideDuration: 2000,
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    } finally {
      dispatch(setShowProviderDrawer(true));
    }
  };

  const handleDeleteConfirmation = async () => {
    try {
      // await deleteProvider({
      //   variables: { providerId: selectedProviderData.id },
      // });

      enqueueSnackbar('Provider was successfully deleted', {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });

      await refetchProviders();
      dispatch(setShowDeleteProviderModal(false));
      dispatch(setShowProviderDrawer(false));
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  const handleSubmitGeneralForm = useMemo(
    () => (e?: BaseSyntheticEvent) => {
      return handleSubmit(async (formData) => {
        try {
          const newFormData = {
            ...formData,
            syncRoundEnd: formData?.syncRoundEnd === 'true',
            retryScheduledSendCredit: formData?.retryScheduledSendCredit === 'true',
            refundOnCreditFail: formData?.refundOnCreditFail === 'true',
            filterExternalProviderBets: formData?.filterExternalProviderBets === 'true',
            isInternal: formData?.isInternal === 'true',
            syncRaindrop: formData?.syncRaindrop === 'true',
            syncFreebet: formData?.syncFreebet === 'true',
            notifyOnLastFreebet: formData?.notifyOnLastFreebet === 'true',
            enableInactivityScreen: formData?.enableInactivityScreen === 'true',
          };
          await updateProvider({
            // @ts-ignore
            variables: { providerId: selectedProviderData?.id, input: newFormData },
          });
          enqueueSnackbar('Provider was successfully updated', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });

          await refetchProviders();

          dispatch(setShowProviderDrawer(false));
        } catch (error) {
          enqueueSnackbar(error.message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        }
      })(e);
    },
    []
  );

  const uploadCurrencies = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('providerId', selectedProviderData?.id);
    const token = Cookies.get('accessToken');
    try {
      await axios.post(
        `${process.env.REACT_APP_UPLOADS_API_PROXY_URL}/uploads/currency-configs-template`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            authorization: token ? `Bearer ${token}` : '',
            'x-rgs-id': rgs,
          },
        }
      );
      refetchProviderConfigs();
      enqueueSnackbar('Currencies were successfully uploaded', {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  return {
    // Store
    selectedCurrency,
    showCurrencyModal,
    showProviderDrawer,
    selectedProviderData,
    showDeleteProviderModal,
    showCancelProviderDrawer,
    submittable: isDirty && Object.values(errors).length == 0,
    // Actions
    setSelectedCurrency,
    setSelectedProvider,
    setShowCurrencyModal,
    setShowProviderDrawer,
    setShowDeleteProviderModal,
    setShowCancelProviderDrawer,
    uploadCurrencies,
    handleCreateCancel,
    handleCancelChanges,
    handleProviderRowClick,
    handleCancelTakeMeBack,
    handleSubmitGeneralForm,
    handleDeleteConfirmation,
  };
};
